<div class="w-full space-y-4">

  <!-- Page Header -->
  <div class="flex items-center justify-between pb-4 border-b border-slate-200">
    <div class="flex items-center gap-3">
      <div class="h-10 w-10 bg-slate-100 rounded-lg flex items-center justify-center">
        <mat-icon class="text-slate-600">receipt_long</mat-icon>
      </div>
      <div>
        <h2 class="text-sm font-bold tracking-widest uppercase text-slate-800">
          Transaction Form
        </h2>
        <p class="text-xs text-slate-500 font-medium mt-0.5">
          Manage transaction details
        </p>
      </div>
    </div>
  </div>

  <!-- ================= TRANSACTION DETAILS ================= -->
  <div class="bg-white rounded-lg shadow-sm border border-slate-300 px-4 py-3">

    <div class="flex items-center gap-2 mb-3 border-b border-slate-100 pb-2">
      <mat-icon class="text-slate-400 text-sm h-5 w-5">info</mat-icon>
      <span class="text-xs font-bold tracking-widest uppercase text-slate-600">
        Transaction
        <ng-container *ngIf="transactionNo"> :
          <span class="border border-indigo-300 text-indigo-700 px-2 py-0.5 rounded text-[13px] font-semibold">
            {{ transactionNo }}
          </span>
        </ng-container>
      </span>
    </div>

    <form #transactionForm="ngForm">

      <div class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-x-4 gap-y-2">

        <!-- Transaction Type -->
        <mat-form-field appearance="outline" class="w-full">
          <mat-label>Transaction Type</mat-label>
          <mat-icon matPrefix class="text-slate-500 mr-2">receipt_long</mat-icon>
          <mat-select name="transactionType" [(ngModel)]="transactionType"
            [disabled]="addedServiceLines.length > 0 || isFormDisabled">
            <mat-option [value]="TransactionType.QUOTATION">Quotation</mat-option>
            <mat-option [value]="TransactionType.DIRECT_ORDER">Direct Order</mat-option>
          </mat-select>
        </mat-form-field>

        <!-- Customer -->
        <mat-form-field appearance="outline" class="w-full">
          <mat-label>Customer</mat-label>
          <mat-icon matPrefix class="text-slate-500 mr-2">person</mat-icon>
          <mat-select name="customerId" [(ngModel)]="selectedCustomerId"
            [disabled]="addedServiceLines.length > 0 || isFormDisabled">
            <mat-option *ngIf="customerList.length === 0" disabled>No customers available</mat-option>
            <mat-option *ngFor="let c of customerList" [value]="c.customerId">
              {{ c.customerName }}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <!-- Transaction Date -->
        <mat-form-field appearance="outline" class="w-full">
          <mat-label>Transaction Date</mat-label>
          <mat-icon matPrefix class="text-slate-500 mr-2">event</mat-icon>
          <input matInput [matDatepicker]="txnDatePicker" name="transactionDate" [(ngModel)]="transactionDate"
            [disabled]="isFormDisabled">
          <mat-datepicker-toggle matSuffix [for]="txnDatePicker" [disabled]="isFormDisabled"></mat-datepicker-toggle>
          <mat-datepicker #txnDatePicker></mat-datepicker>
        </mat-form-field>

        <!-- Total Discount -->
        <div class="w-full">
          <div class="flex items-center justify-between h-[54px] px-4 rounded-lg 
              bg-gradient-to-r from-emerald-50 to-emerald-100 
              border border-emerald-200 shadow-sm">
            <div class="flex items-center gap-2 text-emerald-600">
              <span class="text-sm font-medium">Total Discount:</span>
            </div>
            <span class="text-sm font-medium text-emerald-700">
              ₹ {{ totalDiscount | number:'1.2-2' }}
            </span>
          </div>
        </div>

        <!-- Total Amount -->
        <div class="w-full">
          <div class="flex items-center justify-between h-[54px] px-4 rounded-lg 
              bg-gradient-to-r from-blue-50 to-blue-100 
              border border-blue-200 shadow-sm">
            <div class="flex items-center gap-2 text-blue-600">
              <span class="text-sm font-medium">Total Amount:</span>
            </div>
            <span class="text-sm font-medium text-blue-700">
              ₹ {{ grandTotal | number:'1.2-2' }}
            </span>
          </div>
        </div>

        <!-- GST Transaction Toggle -->
        <!-- Adjusted grid span for better alignment with new chip-like elements.
             The h-[56px] is kept to maintain vertical alignment with other form fields. -->
        <div class="flex items-center h-[56px] md:col-span-1 lg:col-span-1">
          <mat-checkbox #gstCheck name="isGstTransaction" [(ngModel)]="isGstTransaction" (change)="calculateTotals()"
            [disabled]="isFormDisabled">
            <span class="text-xs font-semibold tracking-widest uppercase text-slate-700">
              GST Transaction
            </span>
          </mat-checkbox>
        </div>

        <!-- IGST -->
        <mat-form-field appearance="outline" class="w-full">
          <mat-label>IGST (%)</mat-label>
          <mat-icon matPrefix class="text-slate-500 mr-2">percent</mat-icon>
          <input matInput type="number" min="0" name="igst_percent" [(ngModel)]="igstPercent"
            (ngModelChange)="calculateTotals()" [disabled]="!isGstTransaction || isFormDisabled">
        </mat-form-field>

        <!-- CGST -->
        <mat-form-field appearance="outline" class="w-full">
          <mat-label>CGST (%)</mat-label>
          <mat-icon matPrefix class="text-slate-500 mr-2">percent</mat-icon>
          <input matInput type="number" min="0" name="cgst_percent" [(ngModel)]="cgstPercent"
            (ngModelChange)="calculateTotals()" [disabled]="!isGstTransaction || isFormDisabled">
        </mat-form-field>

        <!-- SGST -->
        <mat-form-field appearance="outline" class="w-full">
          <mat-label>SGST (%)</mat-label>
          <mat-icon matPrefix class="text-slate-500 mr-2">percent</mat-icon>
          <input matInput type="number" min="0" name="sgst_percent" [(ngModel)]="sgstPercent"
            (ngModelChange)="calculateTotals()" [disabled]="!isGstTransaction || isFormDisabled">
        </mat-form-field>

        <!-- Active Toggle -->
        <div class="flex items-center h-[56px]">
          <mat-checkbox [checked]="true" [disabled]="isFormDisabled">
            <span class="text-xs font-semibold tracking-widest uppercase text-slate-700">
              Active
            </span>
          </mat-checkbox>
        </div>

      </div>

    </form>

  </div>

  <!-- ================= ADD SERVICE ================= -->
  <div class="bg-white rounded-lg shadow-sm border border-slate-300 px-4 py-3">

    <div class="flex items-center gap-2 mb-3 border-b border-slate-100 pb-2">
      <mat-icon class="text-slate-400 text-sm h-5 w-5">playlist_add</mat-icon>
      <span class="text-xs font-bold tracking-widest uppercase text-slate-600">
        Add Service
      </span>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-12 gap-x-4 gap-y-2 items-start">

      <!-- ================= ROW 1 ================= -->
      <!-- Service -->
      <div class="md:col-span-2 lg:col-span-2">
        <mat-form-field appearance="outline" class="w-full">
          <mat-label>Service</mat-label>
          <mat-icon matPrefix class="text-slate-500 mr-2">miscellaneous_services</mat-icon>
          <mat-select name="serviceHeaderId" [(ngModel)]="currentServiceLine.serviceHeaderId"
            [disabled]="isServiceLineDisabled" required>
            <mat-option *ngIf="serviceList.length === 0" disabled>No services available</mat-option>
            <mat-option *ngFor="let s of serviceList" [value]="s.serviceHeaderId"
              [disabled]="isServiceAdded(s.serviceHeaderId)">
              {{ s.serviceName }}
            </mat-option>
          </mat-select>
        </mat-form-field>
      </div>

      <!-- Unit -->
      <div class="md:col-span-2 lg:col-span-2">
        <mat-form-field appearance="outline" class="w-full">
          <mat-label>Unit</mat-label>
          <mat-icon matPrefix class="text-slate-500 mr-2">scale</mat-icon>
          <mat-select name="unitId" [(ngModel)]="currentServiceLine.unitId" [disabled]="isServiceLineDisabled" required>
            <mat-option *ngIf="unitList.length === 0" disabled>No units available</mat-option>
            <mat-option *ngFor="let u of unitList" [value]="u.unitId">
              {{ u.unitName }}
            </mat-option>
          </mat-select>
        </mat-form-field>
      </div>

      <!-- Work Units -->
      <div class="md:col-span-2 lg:col-span-2">
        <mat-form-field appearance="outline" class="w-full">
          <mat-label>Work Units</mat-label>
          <input matInput type="text" name="workUnits" [(ngModel)]="currentServiceLine.workUnits"
            [disabled]="isServiceLineDisabled" placeholder="Enter work units">
        </mat-form-field>
      </div>

      <!-- Quantity -->
      <div class="md:col-span-2 lg:col-span-2">
        <mat-form-field appearance="outline" class="w-full">
          <mat-label>Quantity</mat-label>
          <input matInput type="number" min="1" name="quantity" [(ngModel)]="currentServiceLine.quantity"
            (ngModelChange)="currentServiceLine.amount = lFN_CalculateAmount($event, currentServiceLine.rate, currentServiceLine.discountAmount)"
            [disabled]="isServiceLineDisabled" required>
        </mat-form-field>
      </div>

      <!-- Rate -->
      <div class="md:col-span-2 lg:col-span-2">
        <mat-form-field appearance="outline" class="w-full">
          <mat-label>Rate</mat-label>
          <mat-icon matPrefix class="text-slate-500 mr-2">currency_rupee</mat-icon>
          <input matInput type="number" name="rate" [(ngModel)]="currentServiceLine.rate"
            (ngModelChange)="currentServiceLine.amount = lFN_CalculateAmount(currentServiceLine.quantity, $event, currentServiceLine.discountAmount)"
            [disabled]="isServiceLineDisabled" required>
        </mat-form-field>
      </div>

      <!-- Discount -->
      <div class="md:col-span-2 lg:col-span-2">
        <mat-form-field appearance="outline" class="w-full">
          <mat-label>Discount Amount</mat-label>
          <mat-icon matPrefix class="text-slate-500 mr-2">currency_rupee</mat-icon>
          <input matInput type="number" name="discountAmount" [ngModel]="currentServiceLine.discountAmount || 0"
            (ngModelChange)="currentServiceLine.discountAmount=$event; currentServiceLine.amount = lFN_CalculateAmount(currentServiceLine.quantity, currentServiceLine.rate, $event)"
            [disabled]="isServiceLineDisabled">
        </mat-form-field>
      </div>

      <!-- Amount -->
      <div class="md:col-span-2 lg:col-span-2">
        <mat-form-field appearance="outline" class="w-full">
          <mat-label>Amount</mat-label>
          <mat-icon matPrefix class="text-slate-500 mr-2">currency_rupee</mat-icon>
          <input matInput type="number" name="amount" disabled class="font-bold" [ngModel]="currentServiceLine.amount"
            required>
        </mat-form-field>
      </div>

      <!-- ================= ROW 2 ================= -->
      <!-- Start Date -->
      <div class="md:col-span-2 lg:col-span-2">
        <mat-form-field appearance="outline" class="w-full">
          <mat-label>Service Start Date</mat-label>
          <mat-icon matPrefix class="text-slate-500 mr-2">event</mat-icon>
          <input matInput [matDatepicker]="startDatePicker" name="serviceStartDate"
            [(ngModel)]="currentServiceLine.serviceStartDate" (dateChange)="lFN_CalculateExpiry()"
            [disabled]="isServiceLineDisabled">
          <mat-datepicker-toggle matSuffix [for]="startDatePicker"
            [disabled]="isServiceLineDisabled"></mat-datepicker-toggle>
          <mat-datepicker #startDatePicker></mat-datepicker>
        </mat-form-field>
      </div>

      <!-- Expiry Months -->
      <div class="md:col-span-2 lg:col-span-2">
        <mat-form-field appearance="outline" class="w-full">
          <mat-label>Expiry (Months)</mat-label>
          <input matInput type="number" min="1" name="serviceExpiryInMonths"
            [(ngModel)]="currentServiceLine.serviceExpiryInMonths" (ngModelChange)="lFN_CalculateExpiry()"
            [disabled]="isServiceLineDisabled">
        </mat-form-field>
      </div>

      <!-- Expiry Date -->
      <div class="md:col-span-2 lg:col-span-2">
        <mat-form-field appearance="outline" class="w-full">
          <mat-label>Service Expiry Date</mat-label>
          <mat-icon matPrefix class="text-slate-500 mr-2">event_busy</mat-icon>
          <input matInput [matDatepicker]="expiryDatePicker" name="serviceExpiryDate"
            [(ngModel)]="currentServiceLine.serviceExpiryDate" readonly>
          <mat-datepicker-toggle matSuffix [for]="expiryDatePicker" disabled></mat-datepicker-toggle>
          <mat-datepicker #expiryDatePicker></mat-datepicker>
        </mat-form-field>
      </div>

      <!-- Work Description -->
      <div class="md:col-span-2 lg:col-span-3">
        <mat-form-field appearance="outline" class="w-full">
          <mat-label>Work Description</mat-label>
          <mat-icon matPrefix class="text-slate-500 mr-2">description</mat-icon>
          <input matInput name="serviceWorkDescription" [(ngModel)]="currentServiceLine.serviceWorkDescription"
            [disabled]="isServiceLineDisabled" placeholder="Enter work description" required>
        </mat-form-field>
      </div>

      <!-- Custom SMS Checkbox -->
      <!-- <div class="md:col-span-2 lg:col-span-2 flex items-center h-[56px] px-1">
        <div class="flex items-center">
          <mat-checkbox name="scheduleCustomSms" [(ngModel)]="currentServiceLine.scheduleCustomSms"
            (change)="onCustomSmsChange($event)" [disabled]="isServiceLineDisabled">
            <span class="text-xs font-semibold tracking-widest uppercase text-slate-700">Custom SMS</span>
          </mat-checkbox>

          <button *ngIf="currentServiceLine.scheduleCustomSms" type="button" mat-icon-button
            (click)="openCustomSmsPopup()" [matTooltip]="isViewMode ? 'View SMS Dates' : 'Edit SMS Dates'"
            class="text-slate-500 hover:text-indigo-600 transition-colors scale-90 ml-1">
            <mat-icon>{{ isViewMode ? 'visibility' : 'edit_calendar' }}</mat-icon>
          </button>

          <span *ngIf="currentServiceLine.scheduleCustomSms && currentServiceLine.scheduleCustomSmsList?.length"
            class="bg-slate-100 text-slate-600 border border-slate-200 text-[10px] font-bold px-2 py-0.5 rounded-full z-10 ml-1 mt-1">
            {{ currentServiceLine.scheduleCustomSmsList.length }}
          </span>
        </div>
      </div> -->

      <!-- ================= ROW 3 ================= -->
      <div class="md:col-span-6 lg:col-span-12 flex items-center justify-between mt-1">

        <!-- Active Checkbox -->
        <div class="flex items-center pl-1">
          <mat-checkbox [checked]="true" [disabled]="isServiceLineDisabled">
            <span class="text-xs font-semibold tracking-widest uppercase text-slate-700">Active</span>
          </mat-checkbox>
        </div>

        <!-- Buttons -->
        <div class="flex items-center justify-end gap-2" *ngIf="!isViewMode">
          <button type="button" (click)="lFN_ClearServiceLine()" class="px-6 py-2 min-w-[80px] rounded-md border border-slate-300 hover:bg-slate-50
                   text-slate-600 text-[10px] font-bold tracking-widest uppercase transition-colors">
            Clear
          </button>

          <button type="button" (click)="lFN_AddServiceLine()" class="px-8 py-2 min-w-[100px] rounded-md
                   bg-slate-900 hover:bg-slate-800
                   text-white text-[10px] font-bold tracking-widest uppercase shadow transition-colors">
            {{ editIndex !== -1 ? 'Update' : 'Add' }}
          </button>
        </div>
      </div>

    </div>

  </div>

  <!-- ================= SERVICE LINES TABLE ================= -->
  <div class="bg-white rounded-lg shadow-sm border border-slate-300 overflow-x-auto">

    <table mat-table [dataSource]="addedServiceLines" class="w-full">

      <ng-container matColumnDef="srNo">
        <th mat-header-cell *matHeaderCellDef class="bg-gray-900 text-white text-xs uppercase text-center  px-4">
          Sr No.
        </th>
        <td mat-cell *matCellDef="let row; let i = index" class="text-center text-sm font-medium px-4 text-slate-500">
          {{ i + 1 }}
        </td>
      </ng-container>

      <ng-container matColumnDef="service">
        <th mat-header-cell *matHeaderCellDef
          class="bg-gray-900 text-white text-xs uppercase text-left px-4 tracking-wider">
          Service
        </th>
        <td mat-cell *matCellDef="let row" class="text-sm text-left px-4 text-slate-700">
          {{ row.serviceName }}
        </td>
      </ng-container>

      <ng-container matColumnDef="workDescription">
        <th mat-header-cell *matHeaderCellDef
          class="bg-gray-900 text-white text-xs uppercase text-left px-4 tracking-wider">
          Work Description
        </th>
        <td mat-cell *matCellDef="let row" class="text-sm text-left px-4 text-slate-600 truncate max-w-[200px]"
          [matTooltip]="row.serviceWorkDescription">
          {{ row.serviceWorkDescription || '-' }}
        </td>
      </ng-container>

      <ng-container matColumnDef="unit">
        <th mat-header-cell *matHeaderCellDef
          class="bg-gray-900 text-white text-xs uppercase text-left px-4 tracking-wider">
          Unit
        </th>
        <td mat-cell *matCellDef="let row" class="text-sm text-left px-4 text-slate-600">
          {{ row.unit }}
        </td>
      </ng-container>

      <ng-container matColumnDef="workUnits">
        <th mat-header-cell *matHeaderCellDef
          class="bg-gray-900 text-white text-xs uppercase text-left px-4 tracking-wider">
          Work Units
        </th>
        <td mat-cell *matCellDef="let row" class="text-sm text-left px-4 text-slate-600">
          {{ row.workUnits != null ? row.workUnits : '-' }}
        </td>
      </ng-container>

      <ng-container matColumnDef="startDate">
        <th mat-header-cell *matHeaderCellDef
          class="bg-gray-900 text-white text-xs uppercase text-left px-4 tracking-wider">
          Start Date
        </th>
        <td mat-cell *matCellDef="let row" class="text-sm text-left px-4 text-slate-600 whitespace-nowrap">
          {{ row.serviceStartDate | date:'dd-MMM-yyyy' }}
        </td>
      </ng-container>

      <ng-container matColumnDef="expiryDate">
        <th mat-header-cell *matHeaderCellDef
          class="bg-gray-900 text-white text-xs uppercase text-left px-4 tracking-wider">
          Expiry Date
        </th>
        <td mat-cell *matCellDef="let row" class="text-sm text-left px-4 text-slate-600 whitespace-nowrap">
          {{ row.serviceExpiryDate ? (row.serviceExpiryDate | date:'dd-MMM-yyyy') : '-' }}
        </td>
      </ng-container>

      <ng-container matColumnDef="amount">
        <th mat-header-cell *matHeaderCellDef class="bg-gray-900 text-white text-xs uppercase text-right px-4"
          style="text-align: right;">
          Amount
        </th>
        <td mat-cell *matCellDef="let row" class="text-sm text-right px-4 font-medium" style="text-align: right;">
          ₹ {{ row.amount | number:'1.2-2' }}
        </td>
      </ng-container>

      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef
          class="bg-gray-900 text-white text-xs uppercase text-center px-4 tracking-wider">
          Actions
        </th>
        <td mat-cell *matCellDef="let row; let i = index" class="text-center px-4">
          <button type="button" mat-icon-button (click)="editServiceLine(i)" [matTooltip]="isViewMode ? 'View' : 'Edit'"
            class="hover:bg-slate-100 transition-colors">
            <mat-icon class="text-slate-600 text-sm scale-90">{{ isViewMode ? 'visibility' : 'edit' }}</mat-icon>
          </button>
          <button type="button" mat-icon-button (click)="removeServiceLine(i)" matTooltip="Delete"
            class="hover:bg-slate-100 transition-colors" [disabled]="isViewMode">
            <mat-icon class="text-rose-600 text-sm scale-90">delete</mat-icon>
          </button>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
      <tr mat-row *matRowDef="let row; columns: displayedColumns;"
        class="hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0"></tr>

    </table>

  </div>

  <!-- ================= TOTAL SECTION ================= -->
  <div class="bg-white rounded-lg shadow-sm border border-slate-300 px-4 py-3">

    <div class="flex flex-wrap md:flex-row md:items-center md:justify-between gap-4">

      <!-- Totals as Chips -->
      <div class="flex flex-wrap gap-3">
        <div class="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-md">
          <span class="text-sm text-blue-700 font-medium">Subtotal:</span>
          <span class="text-sm text-blue-900 font-medium">₹ {{ subTotal | number:'1.2-2' }}</span>
        </div>

        <div class="flex items-center gap-2 px-3 py-2 bg-red-50 rounded-md">
          <span class="text-sm text-red-700 font-medium">Total Discount:</span>
          <span class="text-sm text-red-900 font-medium">₹ {{ totalDiscount | number:'1.2-2' }}</span>
        </div>

        <div class="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-md">
          <span class="text-sm text-green-700 font-medium">GST Amount:</span>
          <span class="text-sm text-green-900 font-medium">₹ {{ gstAmount | number:'1.2-2' }}</span>
        </div>

        <div class="flex items-center gap-2 px-3 py-2 bg-purple-50 rounded-md">
          <span class="text-sm text-purple-700 font-medium">Taxable Amount:</span>
          <span class="text-sm text-purple-900 font-medium">₹ {{ taxableAmount | number:'1.2-2' }}</span>
        </div>
      </div>

      <!-- Grand Total (more prominent chip) -->
      <div class="flex items-center gap-2 px-3 py-2 bg-indigo-100 rounded-md">
        <span class="text-sm text-black-700 font-medium">Grand Total:</span>
        <span class="text-sm text-black-900 font-medium">₹ {{ grandTotal | number:'1.2-2' }}</span>
      </div>

      <!-- Original buttons div, moved to align with the new layout.
           The *ngIf="!isViewMode" ensures it only appears in edit mode. -->
      <div class="flex gap-4 items-center" *ngIf="!isViewMode">
        <button type="button" (click)="lFN_SaveTransaction()" class="px-6 py-2.5 rounded-md
                 bg-gradient-to-r from-slate-900 to-black hover:from-black hover:to-slate-800
                 text-white text-[11px] font-bold tracking-widest uppercase shadow transition-all">
          Submit Transaction
        </button>
      </div>

    </div>

  </div>

</div>